import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Box,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// Stałe dla typów kamieni
const STONE_TYPES = [
  { value: 'diamond', label: 'Diament' },
  { value: 'ruby', label: 'Rubin' },
  { value: 'sapphire', label: 'Szafir' },
  { value: 'emerald', label: 'Szmaragd' },
  { value: 'amethyst', label: 'Ametyst' },
  { value: 'topaz', label: 'Topaz' },
  { value: 'zircon', label: 'Cyrkon' },
  { value: 'other', label: 'Inny' }
];

// Stałe dla kształtów kamieni
const STONE_SHAPES = [
  { value: 'round', label: 'Okrągły' },
  { value: 'princess', label: 'Princess' },
  { value: 'cushion', label: 'Cushion' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'oval', label: 'Owalny' },
  { value: 'radiant', label: 'Radiant' },
  { value: 'asscher', label: 'Asscher' },
  { value: 'heart', label: 'Serce' },
  { value: 'pear', label: 'Gruszka' },
  { value: 'marquise', label: 'Markiza' },
  { value: 'baguette', label: 'Bagiet' },
  { value: 'trillion', label: 'Trillion' }
];

// Stałe dla kolorów diamentów
const DIAMOND_COLORS = [
  { value: 'D', label: 'D' },
  { value: 'E', label: 'E' },
  { value: 'F', label: 'F' },
  { value: 'G', label: 'G' },
  { value: 'H', label: 'H' },
  { value: 'I', label: 'I' },
  { value: 'J', label: 'J' },
  { value: 'K', label: 'K' },
  { value: 'L', label: 'L' },
  { value: 'M', label: 'M' },
  { value: 'N', label: 'N' }
];

// Stałe dla czystości diamentów
const DIAMOND_CLARITY = [
  { value: 'FL', label: 'FL' },
  { value: 'IF', label: 'IF' },
  { value: 'VVS', label: 'VVS' },
  { value: 'VVS1', label: 'VVS1' },
  { value: 'VVS2', label: 'VVS2' },
  { value: 'VS', label: 'VS' },
  { value: 'VS1', label: 'VS1' },
  { value: 'VS2', label: 'VS2' },
  { value: 'SI', label: 'SI' },
  { value: 'SI1', label: 'SI1' },
  { value: 'SI2', label: 'SI2' },
  { value: 'I1', label: 'I1' },
  { value: 'I2', label: 'I2' },
  { value: 'I3', label: 'I3' }
];

// Stałe dla szlifu, polerowania i symetrii
const STONE_GRADE_OPTIONS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'very_good', label: 'Very Good' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }
];

// Stałe dla kolorów kamieni kolorowych
const COLORED_STONE_COLORS = [
  { value: 'light', label: 'Jasny' },
  { value: 'medium', label: 'Średni' },
  { value: 'dark', label: 'Ciemny' }
];

/**
 * Komponent formularza dla kamieni dodatkowych obsługujący wiele zestawów kamieni
 * 
 * @param {Object} props
 * @param {Object} props.sideStones - Obiekt zawierający informacje o kamieniach dodatkowych
 * @param {boolean} props.sideStones.has_stones - Czy produkt ma kamienie dodatkowe
 * @param {Array} props.sideStones.side_stones_sets - Tablica zestawów kamieni dodatkowych
 * @param {Function} props.onChange - Funkcja wywoływana przy zmianie danych
 */
const SideStonesForm = ({ sideStones, onChange }) => {
  const [sideStonesData, setSideStonesData] = useState(sideStones || { has_stones: false, side_stones_sets: [] });

  useEffect(() => {
    onChange(sideStonesData);
  }, [sideStonesData, onChange]);

  const handleSetChange = (index, e) => {
    const { name, value } = e.target;
    setSideStonesData(prev => {
      const updatedSets = [...prev.side_stones_sets];
      const currentSet = { ...updatedSets[index], [name]: value };

      if (name === 'stone_type') {
        if (value === 'diamond') {
          currentSet.color = '';
          currentSet.clarity = '';
        } else {
          currentSet.color_from = '';
          currentSet.color_to = '';
          currentSet.clarity_from = '';
          currentSet.clarity_to = '';
          currentSet.cut = '';
          currentSet.polish = '';
          currentSet.symmetry = '';
        }
      }
      updatedSets[index] = currentSet;
      return { ...prev, side_stones_sets: updatedSets };
    });
  };

  const addSet = () => {
    setSideStonesData(prev => ({
      ...prev,
      side_stones_sets: [...prev.side_stones_sets, {
        stone_type: '',
        shape: '',
        total_weight: '',
        side_stones_count: '',
        color_from: '',
        color_to: '',
        clarity_from: '',
        clarity_to: '',
        cut: '',
        polish: '',
        symmetry: '',
        color: '',
        clarity: ''
      }]
    }));
  };

  const removeSet = (index) => {
    setSideStonesData(prev => ({
      ...prev,
      side_stones_sets: prev.side_stones_sets.filter((_, i) => i !== index)
    }));
  };

  const handleHasStonesChange = (e) => {
    const { checked } = e.target;
    setSideStonesData(prev => {
      const newData = { ...prev, has_stones: checked };
      if (!checked) {
        newData.side_stones_sets = [];
      } else if (newData.side_stones_sets.length === 0) {
        newData.side_stones_sets = [{
          stone_type: '', shape: '', total_weight: '', side_stones_count: '',
          color_from: '', color_to: '', clarity_from: '', clarity_to: '',
          cut: '', polish: '', symmetry: '', color: '', clarity: ''
        }];
      }
      return newData;
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={sideStonesData.has_stones} onChange={handleHasStonesChange} />}
            label="Produkt posiada kamienie boczne"
          />
        </Grid>

        {sideStonesData.has_stones && sideStonesData.side_stones_sets.map((stoneSet, index) => (
          <Grid item xs={12} key={index}>
            <Card variant="outlined" sx={{ p: 2, mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">Zestaw kamieni bocznych #{index + 1}</Typography>
                  {sideStonesData.side_stones_sets.length > 1 && (
                    <IconButton color="error" onClick={() => removeSet(index)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Typ kamienia</InputLabel>
                      <Select name="stone_type" value={stoneSet.stone_type || ''} onChange={(e) => handleSetChange(index, e)} label="Typ kamienia">
                        {STONE_TYPES.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Kształt</InputLabel>
                      <Select name="shape" value={stoneSet.shape || ''} onChange={(e) => handleSetChange(index, e)} label="Kształt">
                        {STONE_SHAPES.map(shape => <MenuItem key={shape.value} value={shape.value}>{shape.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Ilość kamieni" name="side_stones_count" type="number" value={stoneSet.side_stones_count || ''} onChange={(e) => handleSetChange(index, e)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Łączna masa (ct)" name="total_weight" type="number" value={stoneSet.total_weight || ''} onChange={(e) => handleSetChange(index, e)} />
                  </Grid>

                  {stoneSet.stone_type === 'diamond' && (
                    <>
                      <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Kolor od</InputLabel><Select name="color_from" value={stoneSet.color_from || ''} onChange={(e) => handleSetChange(index, e)} label="Kolor od">{DIAMOND_COLORS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Kolor do</InputLabel><Select name="color_to" value={stoneSet.color_to || ''} onChange={(e) => handleSetChange(index, e)} label="Kolor do">{DIAMOND_COLORS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Czystość od</InputLabel><Select name="clarity_from" value={stoneSet.clarity_from || ''} onChange={(e) => handleSetChange(index, e)} label="Czystość od">{DIAMOND_CLARITY.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Czystość do</InputLabel><Select name="clarity_to" value={stoneSet.clarity_to || ''} onChange={(e) => handleSetChange(index, e)} label="Czystość do">{DIAMOND_CLARITY.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Szlif</InputLabel><Select name="cut" value={stoneSet.cut || ''} onChange={(e) => handleSetChange(index, e)} label="Szlif">{STONE_GRADE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Polerowanie</InputLabel><Select name="polish" value={stoneSet.polish || ''} onChange={(e) => handleSetChange(index, e)} label="Polerowanie">{STONE_GRADE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</Select></FormControl></Grid>
                      <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Symetria</InputLabel><Select name="symmetry" value={stoneSet.symmetry || ''} onChange={(e) => handleSetChange(index, e)} label="Symetria">{STONE_GRADE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</Select></FormControl></Grid>
                    </>
                  )}

                  {stoneSet.stone_type && stoneSet.stone_type !== 'diamond' && (
                     <>
                       <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Intensywność koloru</InputLabel><Select name="color" value={stoneSet.color || ''} onChange={(e) => handleSetChange(index, e)} label="Intensywność koloru">{COLORED_STONE_COLORS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</Select></FormControl></Grid>
                       <Grid item xs={12} md={6}><TextField fullWidth label="Czystość" name="clarity" value={stoneSet.clarity || ''} onChange={(e) => handleSetChange(index, e)} /></Grid>
                     </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {sideStonesData.has_stones && (
          <Grid item xs={12}>
            <Button onClick={addSet} startIcon={<AddIcon />}>Dodaj kolejny zestaw kamieni</Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SideStonesForm;
